/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable no-console */
import React from "react";
import { useDispatch } from "react-redux";
import cuid from "cuid";
import { useNavigate } from "react-router-dom";
import { orderBy } from "lodash";
import {
  CalendarOutlined,
  DownOutlined,
  SearchOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import elGR from "antd/es/date-picker/locale/el_GR";
import moment from "moment";
import { useMediaQuery } from "react-responsive";
import { Alert, Button, DatePicker, Dropdown, Form, Input } from "../../atoms";
import { useFirestoreCollection } from "../../../hooks/useFirestoreCollection";
import { fetchExclusiveHotel64774FromFirestore } from "../../../firestore/firestoreService";
import { IExclusiveHotel64774 } from "../../../common/types";
import { HotelSearchAvailabilityWrapper } from "./Hotel.style";

interface IProps {
  hotelId: string;
  exclusiveOfferIdParam?: string;
  startDate?: string;
  endDate?: string;
  adultNumbers: number;
  childrenNumber: number;
  infant: number;
  onResetCart?: () => void;
}
export const HotelSearchAvailability: React.FC<IProps> = ({
  hotelId,
  exclusiveOfferIdParam,
  startDate,
  endDate,
  adultNumbers,
  childrenNumber,
  infant,
  onResetCart,
}) => {
  const [form] = Form.useForm();
  const history = useNavigate();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({ query: "(max-width: 600px)" });
  const [exclusiveOffers, setExclusiveOffers] = React.useState<
    IExclusiveHotel64774[]
  >([]);
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const maxStayMessage =
    "Δεν επιτρέπονται διαμονές άνω των 15 διανυκτερεύσεων! Παρακαλώ αλλάξετε τις ημερομηνίες ώστε να προχωρήσετε με την Αναζήτηση σας. ";
  const roomsCount = 1;
  const [adultsCount, setAdultsCount] = React.useState(adultNumbers);
  const [childrenCount, setChildrenCount] = React.useState(childrenNumber);
  const [infantCount, setInfantCount] = React.useState(infant);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [dateRange, setDateRange] = React.useState<
    [moment.Moment | null, moment.Moment | null]
  >(() => {
    if (!startDate) return [null, null];
    const start = moment(startDate, "YYYY-MM-DD");
    const end = endDate
      ? moment(endDate, "YYYY-MM-DD")
      : start.clone().add(1, "day");
    return [start, end];
  });
  useFirestoreCollection({
    query: () =>
      fetchExclusiveHotel64774FromFirestore(hotelId, [
        {
          type: "filter",
          name: "status",
          opr: "==",
          value: true,
        },
      ]),
    data: (data: any) => {
      setExclusiveOffers(
        data?.filter(
          (v: IExclusiveHotel64774) =>
            !moment(v.periodEndDate).isBefore(moment()),
        ),
      );
    },
    deps: [dispatch, hotelId],
    local: true,
    shouldExecute: !!hotelId,
  });

  const handleSubmit = async () => {
    try {
      const values: any = await form.validateFields();
      setLoading(true);
      const start = values.checkin
        ? moment(values.checkin).format("YYYY-MM-DD")
        : "";
      const end = values.checkout
        ? moment(values.checkout).format("YYYY-MM-DD")
        : "";
      if (start && end) {
        const nights = moment(end).diff(moment(start), "days");
        if (nights > 15) {
          setLoading(false);
          setError(maxStayMessage);
          return;
        }
      }
      const matchedOffer =
        exclusiveOffers.find(
          (offer) =>
            moment(start).isSameOrAfter(moment(offer.periodStartDate)) &&
            moment(start).isSameOrBefore(moment(offer.periodEndDate)),
        ) || orderBy(exclusiveOffers, ["periodStartDate"])[0];

      const prams = new URLSearchParams({
        exclusiveOfferId: matchedOffer?.id || "",
        adultNumbers: `${adultsCount}`,
        childrenNumber: `${childrenCount}`,
        roomNumber: `${roomsCount}`,
        startDate: start,
        endDate: end,
        checkInCheckOut:
          start && end
            ? `${moment(start).format("D MMM YYYY")} - ${moment(end).format(
                "D MMM YYYY",
              )}`
            : "",
        infant: `${infantCount}`,
        reCall: cuid(),
      });

      history(`/hotel/${hotelId}?${prams.toString()}`);
      onResetCart?.();
      // getExclusiveOfferId(values.exclusiveOfferId);
      setLoading(false);
    } catch (errorInfo) {
      setLoading(false);
      if (typeof errorInfo === "string") {
        setError(errorInfo.toUpperCase());
      } else if (errorInfo instanceof Error) {
        setError(errorInfo.message);
      }
    }
  };
  // React.useEffect(() => form.resetFields, [dateOptions]);
  const occupancySummary = `${adultsCount} Ενήλικες · ${childrenCount} Παιδιά · ${infantCount} Βρέφη`;

  const handleCountChange = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    value: number,
    min: number,
    max?: number,
  ) => {
    let nextValue = value;
    if (nextValue < min) nextValue = min;
    if (max !== undefined && nextValue > max) nextValue = max;
    setter(nextValue);
    form.setFieldsValue({
      adultNumbers:
        setter === setAdultsCount
          ? nextValue
          : form.getFieldValue("adultNumbers"),
      childrenNumber:
        setter === setChildrenCount
          ? nextValue
          : form.getFieldValue("childrenNumber"),
      infant:
        setter === setInfantCount ? nextValue : form.getFieldValue("infant"),
    });
  };

  const handleCheckInChange = (value: moment.Moment | null) => {
    let nextEnd = dateRange[1];
    if (value) {
      if (!nextEnd || nextEnd.isBefore(value, "day")) {
        nextEnd = value.clone().add(1, "day");
      }
    }
    setDateRange([value, nextEnd]);
    form.setFieldsValue({ checkin: value, checkout: nextEnd });
    form.validateFields(["checkout"]);
    if (value && nextEnd) {
      const nights = nextEnd.diff(value, "days");
      setError(nights > 15 ? maxStayMessage : "");
    } else if (error === maxStayMessage) {
      setError("");
    }
  };

  const handleCheckOutChange = (value: moment.Moment | null) => {
    let nextStart = dateRange[0];
    if (value && nextStart && value.isBefore(nextStart, "day")) {
      nextStart = value;
    }
    setDateRange([nextStart, value]);
    form.setFieldsValue({ checkin: nextStart, checkout: value });
    form.validateFields(["checkout"]);
    if (nextStart && value) {
      const nights = value.diff(nextStart, "days");
      setError(nights > 15 ? maxStayMessage : "");
    } else if (error === maxStayMessage) {
      setError("");
    }
  };

  React.useEffect(() => {
    form.setFieldsValue({
      adultNumbers: adultsCount,
      childrenNumber: childrenCount,
      infant: infantCount,
      roomNumber: roomsCount,
    });
  }, [adultsCount, childrenCount, infantCount, roomsCount, form]);

  React.useEffect(() => {
    if (startDate) {
      const start = moment(startDate, "YYYY-MM-DD");
      const end = endDate
        ? moment(endDate, "YYYY-MM-DD")
        : start.clone().add(1, "day");
      setDateRange([start, end]);
      form.setFieldsValue({ checkin: start, checkout: end });
    }
  }, [startDate, endDate, form]);

  const occupancyOverlay = (
    <div className="occupancy-panel">
      <div className="occ-row">
        <span className="occ-label">Ενήλικες (12+)</span>
        <div className="counter">
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setAdultsCount, adultsCount - 1, 1, 8)
            }
          >
            -
          </button>
          <span className="counter-value">{adultsCount}</span>
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setAdultsCount, adultsCount + 1, 1, 8)
            }
          >
            +
          </button>
        </div>
      </div>
      <div className="occ-row">
        <span className="occ-label">Παιδιά (2-11 Ετών)</span>
        <div className="counter">
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setChildrenCount, childrenCount - 1, 0, 6)
            }
          >
            -
          </button>
          <span className="counter-value">{childrenCount}</span>
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setChildrenCount, childrenCount + 1, 0, 6)
            }
          >
            +
          </button>
        </div>
      </div>
      <div className="occ-row">
        <span className="occ-label">Βρέφη (0-23 Μηνών)</span>
        <div className="counter">
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setInfantCount, infantCount - 1, 0, 1)
            }
          >
            -
          </button>
          <span className="counter-value">{infantCount}</span>
          <button
            type="button"
            className="counter-btn"
            onClick={() =>
              handleCountChange(setInfantCount, infantCount + 1, 0, 1)
            }
          >
            +
          </button>
        </div>
      </div>
      <div className="occupancy-info">
        *Οι ηλικίες των παιδιών και ο αριθμός ατόμων που δηλώνετε στην κράτηση
        σας, θα τηρούνται αυστηρά, διαφορετικά ενδέχεται να υπάρχει επιπλέον
        χρέωση κατά την άφιξη σας στο ξενοδοχείο ή ακόμη και να μην σας
        παραχωρηθεί το δωμάτιο. Το γραφείο Poupas for Holidays δεν θα φέρει
        καμία ευθύνη.
      </div>

      <Button className="done-btn" onClick={() => setDropdownOpen(false)} block>
        Τέλος
      </Button>
    </div>
  );

  return (
    <HotelSearchAvailabilityWrapper ref={wrapperRef}>
      <Form
        form={form}
        name="hotelRoomSearch"
        layout="vertical"
        autoComplete="off"
        className="hotel-search-form"
        initialValues={{
          adultNumbers,
          childrenNumber,
          roomNumber: roomsCount,
          infant,
          exclusiveOfferId: exclusiveOfferIdParam,
          checkin: startDate ? moment(startDate) : undefined,
          checkout: endDate
            ? moment(endDate)
            : startDate
              ? moment(startDate).add(1, "day")
              : undefined,
        }}
      >
        <div className="search-row">
          <div className="input-shell date-shell">
            <span className="field-icon-lg">
              <CalendarOutlined />
            </span>
            <div className="date-grid">
              <div className="date-field">
                <Form.Item
                  name="checkin"
                  className="no-label"
                  rules={[
                    {
                      required: true,
                      message: "Επιλέξτε άφιξη",
                    },
                  ]}
                >
                  <DatePicker
                    value={dateRange[0] as any}
                    onChange={(value) => handleCheckInChange(value)}
                    format="D MMM YYYY"
                    locale={elGR}
                    allowClear={false}
                    getPopupContainer={() =>
                      wrapperRef.current || document.body
                    }
                    disabledDate={(current) =>
                      !!current && current < moment().startOf("day")
                    }
                    popupClassName="hotel-date-popup"
                    placement="bottomLeft"
                    inputReadOnly={isMobile}
                  />
                </Form.Item>
              </div>
              <div className="date-divider" />
              <div className="date-field">
                <Form.Item
                  name="checkout"
                  className="no-label"
                  rules={[
                    {
                      required: true,
                      message: "Επιλέξτε αναχώρηση",
                    },
                  ]}
                >
                  <DatePicker
                    value={dateRange[1] as any}
                    onChange={(value) => handleCheckOutChange(value)}
                    format="D MMM YYYY"
                    locale={elGR}
                    allowClear={false}
                    getPopupContainer={() =>
                      wrapperRef.current || document.body
                    }
                    disabledDate={(current) => {
                      if (!current) return false;
                      const today = moment().startOf("day");
                      if (current < today) return true;
                      if (dateRange[0]) {
                        return current.isBefore(dateRange[0], "day");
                      }
                      return false;
                    }}
                    popupClassName="hotel-date-popup"
                    placement="bottomLeft"
                    inputReadOnly={isMobile}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
          <Dropdown
            overlay={occupancyOverlay}
            trigger={["click"]}
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            getPopupContainer={() => wrapperRef.current || document.body}
            placement="bottomLeft"
          >
            <div className="input-shell occupancy-shell">
              <span className="field-icon-lg">
                <UserAddOutlined />
              </span>
              <div className="occupancy-label">{occupancySummary}</div>
              <DownOutlined className="dropdown-icon" />
            </div>
          </Dropdown>
          <Button
            type="primary"
            className="search-button"
            onClick={handleSubmit}
            loading={loading}
          >
            <SearchOutlined /> Έλεγχος Διαθεσιμότητας
          </Button>
        </div>
        <Form.Item name="adultNumbers" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="childrenNumber" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="infant" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="roomNumber" hidden>
          <Input type="hidden" />
        </Form.Item>
      </Form>
      {error !== "" && <Alert message={error} type="error" />}
    </HotelSearchAvailabilityWrapper>
  );
};
